import {
  ANNOTATION_LOCATION,
  ANNOTATION_SOURCE_LOCATION,
  Entity,
  parseLocationRef,
} from '@backstage/catalog-model';
import { ScmIntegrationRegistry } from '@backstage/integration';

export const PROJECT_SLUG_ANNOTATION = 'github.com/project-slug';

export type GithubRepository = {
  owner: string;
  name: string;
  host: string;
  apiBaseUrl: string;
  webUrl: string;
};

export const isGithubAvailable = (entity: Entity) =>
  entity.kind.toLowerCase() === 'component' &&
  !!entity.metadata.annotations?.[PROJECT_SLUG_ANNOTATION]?.trim();

export function getGithubRepository(
  entity: Entity,
  integrations: ScmIntegrationRegistry,
): GithubRepository {
  const slug = entity.metadata.annotations?.[PROJECT_SLUG_ANNOTATION]?.trim();
  const match = slug?.match(/^([a-z\d][a-z\d-]*)\/([a-z\d_.-]+)$/i);
  if (!match || ['.', '..'].includes(match[2])) {
    throw new Error(
      'Set github.com/project-slug to the repository owner/name on this Component.',
    );
  }

  let host = 'github.com';
  const source =
    entity.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION] ??
    entity.metadata.annotations?.[ANNOTATION_LOCATION];
  if (source) {
    const location = parseLocationRef(source);
    if (location.type === 'url') {
      const integration = integrations.github.byUrl(location.target);
      if (!integration) {
        throw new Error(
          'The repository host has no configured GitHub integration.',
        );
      }
      host = integration.config.host;
    }
  }
  const integration = integrations.github.byHost(host);
  if (!integration) {
    throw new Error(`No GitHub integration is configured for ${host}.`);
  }
  return {
    owner: match[1],
    name: match[2],
    host,
    apiBaseUrl: (
      integration.config.apiBaseUrl ??
      (host === 'github.com'
        ? 'https://api.github.com'
        : `https://${host}/api/v3`)
    ).replace(/\/$/, ''),
    webUrl: `https://${host}/${match[1]}/${match[2]}`,
  };
}
